/**
 * productTarget — THE canonical description of "which products is this operation about?" (F2).
 *
 * ⛔⛔ WHY A SHARED MODEL RATHER THAN A SHARED WIDGET. Three surfaces each grew their own
 * selection state, with three vocabularies for the same idea:
 *     TheOffering.product.tsx          selectedProductIds : Set  +  allProductsSelected
 *     ContentReviewDashboard.product   selectedItems            +  selectAllItemsAcrossPages
 *     ProductSelectionModal            productIds (modal-local) +  mode
 * A shared *component* would have left each of them converting to and from its own shape at the
 * boundary, which is where the last eight review rounds' worth of defects lived. The frame has to
 * be the DATA, so every surface speaks one language and the conversions disappear rather than
 * multiply.
 *
 * 🔑 THE ONE DESIGN DECISION THAT MATTERS, AND IT COMES FROM A DOCUMENTED INCIDENT.
 * `POST /batch-enrich-async` takes a parameter named `productIds` — and it wants
 * knowledge_base_entries ids. The route's own comment records the cost:
 *
 *     "The name is the trap: the parameter is `productIds`, and it takes knowledge_base_entries
 *      ids. The route's own variable is called invalidKbeIds — the code knew what it wanted and
 *      the API name did not say it. TWO AGENTS passed valid, active product_feed_items ids (the
 *      obvious reading of the name) and got success with zero work."
 *
 * ⇒ So a canonical control that emitted a bare `productIds: string[]` would INDUSTRIALISE that
 * ambiguity: one shape, adopted by every surface, silently meaning different things per endpoint.
 * A ProductTarget therefore carries its ID KIND. Converting to a wire shape REQUIRES naming the
 * kind the endpoint wants, and a mismatch THROWS rather than sending plausible-looking ids that
 * produce a cheerful zero-work success.
 *
 * ⚠️ NOT a React component and deliberately free of React imports: the model has to be testable
 * without a DOM, and the surfaces adopt it incrementally. The picker UI is built on top of this.
 */

/**
 * Which table's primary keys these ids are.
 *
 * `kbe`  — knowledge_base_entries.id. What batch-enrich-async, content-review and the AEO paths
 *          actually consume, whatever their parameter is called.
 * `feed` — product_feed_items.id. What the product listing shows and what an operator copying an
 *          id off the screen is most likely holding.
 */
export type ProductIdKind = 'kbe' | 'feed';

/** An explicit list of products the operator picked or pasted. */
export interface ExplicitTarget {
  mode: 'explicit';
  idKind: ProductIdKind;
  ids: string[];
}

/**
 * "Everything matching what is on screen", WITHOUT enumerating it.
 *
 * ⚠️ THE FILTER IS THE TARGET — the ids are not resolved here on purpose. A select-all that
 * expands to the CURRENT PAGE's ids is the defect this shape exists to prevent: the operator asks
 * for 14,790 products and gets the 50 they can see, with nothing saying so. Whoever consumes a
 * cohort must either send the filter to an endpoint that understands it, or resolve it through the
 * same query that produced the display — never through a second, hand-written copy of the filter.
 */
export interface CohortTarget {
  mode: 'cohort';
  filter: {
    siteId: string;
    categoryPath?: string | null;
    subcategory?: string | null;
    search?: string | null;
    enriched?: boolean | null;
    hasAeo?: boolean | null;
  };
  /** What the UI believes the filter matches, for confirmation copy. NEVER used as the target. */
  estimatedCount?: number | null;
}

export type ProductTarget = ExplicitTarget | CohortTarget;

/** Options every target-consuming operation shares. */
export interface TargetOptions {
  /** Re-run work that is already done. Explicitly tri-state-free: absent means false. */
  force?: boolean;
  /**
   * ⛔⛔ THE CALLER ASSERTS THAT THE ENDPOINT RESOLVES THIS FILTER **KIND-AWARELY**.
   *
   * A cohort is a FILTER, and a filter carries no id kind — so unlike an explicit target it slips
   * past the mismatch check entirely. `GET /offering` matches products from more than one table, so
   * a filter that matches 8,201 products on Motocard designates 6,143 the enrichment endpoint cannot
   * take. The kind guard cannot see it: there is no kind to compare.
   * ⇒ So the guarantee has to come from the RESOLVER, and it has to be STATED. Absent this flag a
   *   cohort request THROWS. That makes the unsafe case the default and forces whoever wires a new
   *   endpoint to answer the question rather than inherit an assumption.
   */
  cohortResolutionIsKindAware?: boolean;
}

export const isExplicit = (t: ProductTarget): t is ExplicitTarget => t.mode === 'explicit';
export const isCohort = (t: ProductTarget): t is CohortTarget => t.mode === 'cohort';

/** How many products the target names, or null when only the server can know. */
export function targetSize(t: ProductTarget): number | null {
  return isExplicit(t) ? t.ids.length : (t.estimatedCount ?? null);
}

/**
 * Parse a pasted list into an explicit target.
 *
 * ⚠️ SEPARATOR-TOLERANT ON PURPOSE: an operator pastes from a spreadsheet column (newlines), a CSV
 * (commas), a chat message (spaces) or a SQL result (tabs, quotes, trailing semicolons). Rejecting
 * any of those teaches them to hand-clean the list, which is where transcription errors enter.
 *
 * ⚠️ DEDUPES, AND REPORTS THAT IT DID. A duplicated id is not an error — spreadsheets produce them
 * — but silently collapsing 60 pasted lines into 58 products, then reporting "58 queued", reads as
 * the tool losing two. The count difference has to be visible to the caller.
 */
export function parsePastedIds(raw: string, idKind: ProductIdKind): {
  target: ExplicitTarget;
  pastedCount: number;
  duplicatesRemoved: number;
  rejected: string[];
} {
  const tokens = String(raw ?? '')
    .split(/[\s,;]+/)
    .map((t) => t.trim().replace(/^["']|["']$/g, ''))
    .filter((t) => t !== '');

  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const accepted: string[] = [];
  const rejected: string[] = [];
  const seen = new Set<string>();
  let duplicatesRemoved = 0;

  for (const token of tokens) {
    if (!UUID.test(token)) {
      // ⇒ REPORTED, NOT DROPPED. A malformed id silently discarded means the operator asked for 60
      // products, got 59, and has no way to learn which one never ran.
      rejected.push(token);
      continue;
    }
    const key = token.toLowerCase();
    if (seen.has(key)) {
      duplicatesRemoved += 1;
      continue;
    }
    seen.add(key);
    accepted.push(token);
  }

  return {
    target: { mode: 'explicit', idKind, ids: accepted },
    pastedCount: tokens.length,
    duplicatesRemoved,
    rejected,
  };
}

/**
 * Build the request body for an endpoint, given the ID KIND that endpoint consumes.
 *
 * ⛔⛔ THE MISMATCH THROWS. It does not convert, and it does not pass the ids through with a
 * warning. Sending product_feed_items ids to a route that wants knowledge_base_entries ids is the
 * documented failure that returned HTTP 200 with zero work done, twice — a shape that looks like
 * success is far worse than an exception, because nothing downstream ever asks again.
 *
 * ⇒ Callers that legitimately hold the other kind must resolve them deliberately (an explicit
 * lookup), which is a visible step in their code rather than a silent assumption in this one.
 */
export function toRequestBody(
  target: ProductTarget,
  endpointIdKind: ProductIdKind,
  options: TargetOptions = {}
): Record<string, unknown> {
  const force = options.force === true;

  if (isCohort(target)) {
    /**
     * ⛔⛔ A COHORT CARRIES NO ID KIND, SO THE MISMATCH GUARD ABOVE CANNOT PROTECT IT. That guard is
     * the reason ProductTarget exists, and the cohort branch returned before ever reaching it — an
     * explicit target holding the wrong kind throws, while a FILTER matching the same wrong products
     * sailed through. I guarded one branch and left its twin, in the very function written to stop
     * this class.
     * ⇒ The filter cannot be narrowed here (resolving it client-side is the defect the cohort exists
     *   to prevent), so the only honest options are: the RESOLVER guarantees kind-awareness, or the
     *   request is refused. It is refused unless the caller states the guarantee.
     */
    if (options.cohortResolutionIsKindAware !== true) {
      throw new Error(
        `Refusing to send a cohort to a ${endpointIdKind}-id endpoint that has not declared it `
        + 'resolves the filter kind-awarely. A cohort is a FILTER and carries no id kind, so the '
        + 'mismatch check cannot see it: the filter can match products this endpoint cannot take, '
        + 'and the request would report success while doing partial or zero work. Pass '
        + 'cohortResolutionIsKindAware: true only once the endpoint filters by id kind and reports '
        + 'what it excluded.'
      );
    }
    // ⇒ The filter travels, never a resolved id list. See CohortTarget's note: resolving here
    // would resolve against whatever THIS component knows, which is the page, not the query.
    return {
      siteId: target.filter.siteId,
      ...(target.filter.categoryPath ? { categoryPath: target.filter.categoryPath } : {}),
      ...(target.filter.subcategory ? { subcategory: target.filter.subcategory } : {}),
      ...(target.filter.search ? { search: target.filter.search } : {}),
      ...(target.filter.enriched != null ? { enriched: target.filter.enriched } : {}),
      ...(target.filter.hasAeo != null ? { hasAeo: target.filter.hasAeo } : {}),
      forceReEnrich: force,
    };
  }

  if (target.idKind !== endpointIdKind) {
    throw new Error(
      `ProductTarget holds ${target.idKind} ids but this endpoint consumes ${endpointIdKind} ids. `
      + 'Refusing to send them: the parameter is named productIds on both sides, so a mismatch does '
      + 'not fail — it returns success with zero work done. Resolve the ids explicitly first.'
    );
  }

  if (target.ids.length === 0) {
    throw new Error(
      'ProductTarget is empty. Refusing to build a request: an empty id list is indistinguishable '
      + 'from "everything" on some endpoints, and from "nothing" on others.'
    );
  }

  return { productIds: target.ids, forceReEnrich: force };
}
